import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Sparkles } from "lucide-react";
import { api } from "@/lib/kfmApi";

// Broadcast a fresh balance after any credit spend so every meter updates instantly.
export function announceCredits(credits_total) {
  try {
    window.dispatchEvent(new CustomEvent("owp:credits", { detail: { credits_total } }));
  } catch (e) { /* ignore */ }
}

function useCredits() {
  const [sub, setSub] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/me/subscription").then(({ data }) => { if (alive) setSub(data); }).catch(() => {});
    const onUpd = (e) => {
      const t = e?.detail?.credits_total;
      if (t === undefined) return;
      setSub((s) => (s ? { ...s, credits_total: t } : s));
    };
    window.addEventListener("owp:credits", onUpd);
    return () => { alive = false; window.removeEventListener("owp:credits", onUpd); };
  }, []);
  return sub;
}

// Compact pill for the sidebar / header — always visible.
export default function CreditMeter({ className = "" }) {
  const sub = useCredits();
  const navigate = useNavigate();
  if (!sub) return null;
  const uncapped = sub.uncapped;
  const trial = sub.trial?.active;
  const total = sub.credits_total;
  const low = !uncapped && !trial && typeof total === "number" && total <= 3;

  return (
    <button
      type="button"
      data-testid="credit-meter"
      onClick={() => navigate("/billing")}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm border transition-colors text-left w-full
        ${low ? "bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/15"
              : "bg-[#0E131F] border-[#1E273D] text-slate-300 hover:bg-[#0B0F16] border-[#29344D]"} ${className}`}
      title="Study credits — tap to manage"
    >
      <Coins className="w-5 h-5 shrink-0" strokeWidth={1.5} />
      <span className="flex flex-col leading-tight">
        {uncapped ? (
          <span className="font-semibold" data-testid="credit-meter-value">Unlimited credits</span>
        ) : trial ? (
          <>
            <span className="font-semibold" data-testid="credit-meter-value">Trial · unmetered</span>
            <span className="text-[11px] font-normal opacity-70">{sub.trial.days_left} days left</span>
          </>
        ) : (
          <>
            <span className="font-semibold" data-testid="credit-meter-value">{total} credits left</span>
            <span className="text-[11px] font-normal opacity-70">{low ? "Running low — top up" : "this month"}</span>
          </>
        )}
      </span>
    </button>
  );
}

// Tiny inline label next to a Zone B action button (e.g. "costs 1 credit · 14 left").
export function CreditCost({ cost, className = "" }) {
  const sub = useCredits();
  if (!sub) return null;
  const free = sub.uncapped || sub.trial?.active;
  return (
    <span data-testid="credit-cost" className={`inline-flex items-center gap-1 text-xs text-slate-500 ${className}`}>
      {free ? (
        <><Sparkles className="w-3 h-3" strokeWidth={1.75} /> {sub.trial?.active ? "Free during your trial" : "Included"}</>
      ) : (
        <>Costs {cost} credit{cost === 1 ? "" : "s"} · {sub.credits_total} left</>
      )}
    </span>
  );
}
